/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { DataState } from '#UNSAFE_Collection';
import { getClientHints } from '#utils/PRIVATE_clientHints';
import type { Item } from '#utils/UNSAFE_dataProvider';

/**
 * Helper function to determine whether the current device is a mobile device
 * @returns true if running on a mobile device, false otherwise
 */
export function isMobile() {
  const deviceType = getClientHints().deviceType;
  return deviceType === 'phone' || deviceType === 'tablet';
}

/**
 * Helper function to determine whether the current device is a phone
 * @returns true if running on a phone, false otherwise
 */
export function isPhone() {
  return getClientHints().deviceType === 'phone';
}

/**
 * Helper function to determine whether the current device is a tablet
 * @returns true if running on a tablet, false otherwise
 */
export function isTablet() {
  return getClientHints().deviceType === 'tablet';
}

export function isBeforeDataFetch<K, D>(data: DataState<K, D>) {
  return data.sizePrecision === 'atLeast' && data.totalSize === 0;
}

export function isSearchTextEmptyOrUndefined(searchText?: string) {
  return searchText === undefined || searchText.length === 0;
}

export function isSearchClearedFirstTime(prevSearchText?: string, searchText?: string) {
  return prevSearchText === undefined && searchText === '';
}

/**
 * Extract keys from a value items array.
 *
 * @param valueItems The value items from which the keys are to be extracted
 * @returns The set of keys
 */
export function createKeysFromValueItems<K, D>(valueItems?: Item<K, D>[]) {
  const keys = valueItems?.reduce((accumKeys, currentItem) => {
    accumKeys.add(currentItem.key);
    return accumKeys;
  }, new Set<K>());
  return keys;
}
